// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useForgotPassword } from '../../api/auth.js';
import { BrandSymbol, BrandWordmark } from '../../components/BrandMark.js';

/**
 * Request a password-reset email (intakeform-preview-r5t). The API never
 * reveals whether the address exists, so on success we always show the same
 * neutral confirmation.
 */
export function ForgotPasswordRoute() {
  const forgot = useForgotPassword();
  const [email, setEmail] = useState('');

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    forgot.mutate({ email });
  };

  return (
    <main className="login-main" id="main-content">
      <div className="login-card">
        <div className="login-brand">
          <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
          <BrandWordmark className="brand-wordmark" />
        </div>
        <h1 className="login-title">Reset your password</h1>
        {forgot.isSuccess ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p role="status" aria-live="polite" className="text-sm">
              If an account exists for that email, we&rsquo;ve sent a link to reset your password.
              Check your inbox.
            </p>
            <Link to="/login" className="text-sm">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} aria-describedby={forgot.error ? 'forgot-error' : undefined}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p className="text-sm text-muted-foreground" style={{ margin: 0 }}>
                Enter your email and we&rsquo;ll send you a link to set a new password.
              </p>
              <label htmlFor="forgot-email">
                <span>Email</span>
                <input
                  id="forgot-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  aria-required="true"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  style={{ width: '100%' }}
                />
              </label>
              {forgot.error ? (
                <div id="forgot-error" className="error-banner" role="alert" aria-live="assertive">
                  Something went wrong. Please try again.
                </div>
              ) : null}
              <button
                type="submit"
                data-variant="primary"
                disabled={forgot.isPending}
                style={{ width: '100%' }}
              >
                {forgot.isPending ? 'Sending…' : 'Send reset link'}
              </button>
              <Link to="/login" className="text-sm" style={{ textAlign: 'center' }}>
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
